import { createContext, useContext } from 'react'

// AI-CONTEXT: See docs/features/dashboard-empty-states.md
//
// Carries the analytics empty-state context down to the shared chart layer
// (Visualisations/*) without threading a prop through every intermediate
// component. The chart leaves render the standardized empty visual everywhere,
// but the CTA button only appears where `showCta` is true.
//
// Who provides it:
//   - Dashboard analytics cards (DashboardVisualisationCard wrappers) → showCta: true
//   - Analyse-page card bodies (AnalysisResearch/Effect/ActivityCardBody)  → showCta: true
//   - Public LiveReports & print views render the charts WITHOUT a provider,
//     so they fall back to this default (no CTA, generic copy).

/**
 * @typedef {object} EmptyStateContextValue
 * @property {string} [type] - Analytics card type for copy/CTA lookup (e.g. 'research' | 'effect' | 'indicator' | 'output' | 'activity').
 * @property {boolean} showCta - Whether the empty-state CTA button should render.
 * @property {boolean} [sankeyAvailable] - Whether the shown matrix indicator has persona-paired transitions (gates the sankey graph-type icon).
 * @property {string} [headline] - Overrides the empty-state headline (set when a period is active — see periodEmptyState)
 * @property {string} [subtext] - Overrides the supporting sentence
 * @property {(available: boolean) => void} [setSankeyAvailable] - Published by the chart layer (MatrixData) so the card's graph-type picker can hide the sankey icon.
 */

const EmptyStateContext = createContext(
    /** @type {EmptyStateContextValue} */ ({ type: undefined, showCta: false, sankeyAvailable: false, setSankeyAvailable: undefined })
)

export const EmptyStateProvider = EmptyStateContext.Provider

export const useEmptyStateContext = () => useContext(EmptyStateContext)

export default EmptyStateContext
